import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { TransactionService } from '../../services/transactionService';

interface TransactionStatusLabelProps {
  orderId: string;
  transactionId?: string | null;
  paidStatus: 'pending' | 'succeeded' | 'failed' | 'cancelled' | 'refunded' | 'requires_action';
  onClick: () => void;
}

const TransactionStatusLabel: React.FC<TransactionStatusLabelProps> = ({
  orderId,
  transactionId,
  paidStatus,
  onClick,
}) => {
  const [hasTransaction, setHasTransaction] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const checkTransaction = async () => {
      if (paidStatus !== 'succeeded') {
        setHasTransaction(null);
        return;
      }

       if (transactionId) {
        setHasTransaction(true);
        return;
      }

      setChecking(true);
      try {
        const transactions = await TransactionService.getTransactionsByOrderId(orderId);
        setHasTransaction(transactions && transactions.length > 0);
      } catch (error) {
        console.error('Error checking transaction:', error);
        setHasTransaction(false);
      } finally {
        setChecking(false);
      }
    };

    checkTransaction();
  }, [orderId, paidStatus, transactionId]);

  // Only show label if payment succeeded
  if (paidStatus !== 'succeeded') {
    return null;
  }

  // Determine if there's a problem (payment succeeded but no transaction)
  const hasProblem = hasTransaction === false;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.75,
        mb: 1,
        borderRadius: 2,
        background: hasProblem
          ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
          : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: hasProblem
          ? '0 2px 8px rgba(255, 152, 0, 0.3)'
          : '0 2px 8px rgba(76, 175, 80, 0.3)',
        animation: hasProblem ? 'warningPulse 2s infinite' : 'none',
        '@keyframes warningPulse': {
          '0%': {
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
          },
          '50%': {
            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.5)',
          },
          '100%': {
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
          },
        },
        '&:hover': {
          background: hasProblem
            ? 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)'
            : 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)',
          boxShadow: hasProblem
            ? '0 4px 12px rgba(255, 152, 0, 0.4)'
            : '0 4px 12px rgba(76, 175, 80, 0.4)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {hasProblem ? (
        <WarningIcon sx={{ fontSize: 18 }} />
      ) : (
        <CheckCircleIcon sx={{ fontSize: 18 }} />
      )}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {hasProblem ? 'Transaction Issue' : 'Transaction Complete'}
      </Typography>
    </Box>
  );
};

export default TransactionStatusLabel;

